import { inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { map, Observable } from 'rxjs';
import {
  NotificationSchedule,
  NotificationScheduleSpec,
  NotificationScheduleUpdate,
} from 'src/app/models/notifications';
import {
  mutateWithOptionalToken,
  queryWithOptionalToken,
} from '../token-based-action';

const LIST_NOTIFICATION_SCHEDULES_QUERY = gql`
  query notificationSchedules($organizationId: ID!, $memberId: ID!) {
    notificationSchedules(
      organizationId: $organizationId
      memberId: $memberId
    ) {
      id
      name
      type
      description
      cronExpression
      enabled
    }
  }
`;

const SEND_TEST_NOTIFICATION_FOR_SCHEDULE_MUTATION = gql`
  mutation sendTestNotificationForSchedule(
    $organizationId: ID!
    $scheduleId: ID!
  ) {
    sendTestNotificationForSchedule(
      organizationId: $organizationId
      scheduleId: $scheduleId
    )
  }
`;

const DELETE_NOTIFICATION_SCHEDULE_MUTATION = gql`
  mutation deleteNotificationSchedule($organizationId: ID!, $scheduleId: ID!) {
    deleteNotificationSchedule(
      organizationId: $organizationId
      scheduleId: $scheduleId
    )
  }
`;

const UPDATE_NOTIFICATION_SCHEDULE_MUTATION = gql`
  mutation updateNotificationSchedule(
    $organizationId: ID!
    $scheduleId: ID!
    $spec: UpdateNotificationScheduleSpec!
  ) {
    updateNotificationSchedule(
      organizationId: $organizationId
      scheduleId: $scheduleId
      spec: $spec
    ) {
      id
      name
      type
      description
      cronExpression
      enabled
    }
  }
`;

const CREATE_NOTIFICATION_SCHEDULE_MUTATION = gql`
  mutation createNotificationSchedule(
    $organizationId: ID!
    $memberId: ID!
    $spec: NotificationScheduleSpec!
  ) {
    createNotificationSchedule(
      organizationId: $organizationId
      memberId: $memberId
      spec: $spec
    ) {
      id
      name
      type
      description
      cronExpression
      enabled
    }
  }
`;

@Injectable()
export class NotificationsService {
  private apollo = inject(Apollo);

  listNotificationSchedules(
    organizationId: string,
    memberId: string,
    token?: string
  ): Observable<NotificationSchedule[]> {
    return this.apollo
      .query<{ notificationSchedules: NotificationSchedule[] }>(
        queryWithOptionalToken(
          {
            query: LIST_NOTIFICATION_SCHEDULES_QUERY,
            variables: {
              organizationId: organizationId,
              memberId: memberId,
            },
          },
          token
        )
      )
      .pipe(
        map(data => {
          if (data.error) {
            throw new Error(data.error.message);
          }
          return data.data!.notificationSchedules!;
        })
      );
  }

  deleteNotificationSchedule(
    organizationId: string,
    scheduleId: string,
    token?: string
  ): Observable<boolean> {
    return this.apollo
      .mutate<{ deleteNotificationSchedule: boolean }>(
        mutateWithOptionalToken<{ deleteNotificationSchedule: boolean }>(
          {
            mutation: DELETE_NOTIFICATION_SCHEDULE_MUTATION,
            variables: {
              organizationId: organizationId,
              scheduleId: scheduleId,
            },
          },
          token
        )
      )
      .pipe(
        map(data => {
          if (data.error) {
            throw new Error(data.error.message);
          }
          return data.data!.deleteNotificationSchedule;
        })
      );
  }

  updateNotificationSchedule(
    organizationId: string,
    scheduleId: string,
    spec: NotificationScheduleUpdate,
    token?: string
  ): Observable<NotificationSchedule> {
    return this.apollo
      .mutate<{ updateNotificationSchedule: NotificationSchedule }>(
        mutateWithOptionalToken<{
          updateNotificationSchedule: NotificationSchedule;
        }>(
          {
            mutation: UPDATE_NOTIFICATION_SCHEDULE_MUTATION,
            variables: {
              organizationId: organizationId,
              scheduleId: scheduleId,
              spec: spec,
            },
          },
          token
        )
      )
      .pipe(
        map(data => {
          if (data.error) {
            throw new Error(data.error.message);
          }
          return data.data!.updateNotificationSchedule;
        })
      );
  }

  createNotificationSchedule(
    organizationId: string,
    memberId: string,
    spec: NotificationScheduleSpec
  ): Observable<NotificationSchedule> {
    return this.apollo
      .mutate<{ createNotificationSchedule: NotificationSchedule }>({
        mutation: CREATE_NOTIFICATION_SCHEDULE_MUTATION,
        variables: {
          organizationId: organizationId,
          memberId: memberId,
          spec: spec,
        },
      })
      .pipe(
        map(data => {
          if (data.error) {
            throw new Error(data.error.message);
          }
          return data.data!.createNotificationSchedule;
        })
      );
  }

  sendTestNotificationForSchedule(
    organizationId: string,
    scheduleId: string
  ): Observable<boolean> {
    return this.apollo
      .mutate<{ sendTestNotificationForSchedule: boolean }>({
        mutation: SEND_TEST_NOTIFICATION_FOR_SCHEDULE_MUTATION,
        variables: {
          organizationId: organizationId,
          scheduleId: scheduleId,
        },
      })
      .pipe(
        map(data => {
          if (data.error) {
            throw new Error(data.error.message);
          }
          return data.data!.sendTestNotificationForSchedule;
        })
      );
  }
}
